import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { PublicLayout } from './components/layout/PublicLayout'
import { ThemeProvider } from './theme/ThemeProvider'
import { AuthProvider } from './client/AuthProvider'
import { ClientLayout } from './client/components/ClientLayout'
import { MoreMenu } from './client/components/ClientNav'
import { DashboardPage } from './client/pages/DashboardPage'
import { ClientLoginPage } from './client/pages/ClientLoginPage'
import { ClientRegisterPage } from './client/pages/ClientRegisterPage'
import { ClientVerifyEmailPage } from './client/pages/ClientVerifyEmailPage'
import { ClientForgotPasswordPage } from './client/pages/ClientForgotPasswordPage'
import { ClientResetPasswordPage } from './client/pages/ClientResetPasswordPage'
import { AccountsPage } from './client/pages/AccountsPage'
import { DepositsPage } from './client/pages/DepositsPage'
import { WithdrawalsPage } from './client/pages/WithdrawalsPage'
import { TransactionsPage } from './client/pages/TransactionsPage'
import { TradingHistoryPage } from './client/pages/TradingHistoryPage'
import { SupportPage } from './client/pages/SupportPage'
import { ProfilePage } from './client/pages/ProfilePage'
import { LaunchMt5Page } from './client/pages/LaunchMt5Page'
import { AdminAuthProvider } from './admin/AuthProvider'
import { AdminLayout } from './admin/components/AdminLayout'
import { AdminLoginPage } from './admin/pages/AdminLoginPage'
import { AdminForgotPasswordPage } from './admin/pages/AdminForgotPasswordPage'
import { AdminResetPasswordPage } from './admin/pages/AdminResetPasswordPage'
import { AdminDashboardPage } from './admin/pages/AdminDashboardPage'
import { AdminClientsPage } from './admin/pages/AdminClientsPage'
import { AdminClientDetailPage } from './admin/pages/AdminClientDetailPage'
import { AdminTradingAccountsPage } from './admin/pages/AdminTradingAccountsPage'
import { AdminTradingAccountDetailPage } from './admin/pages/AdminTradingAccountDetailPage'
import { AdminDepositsPage } from './admin/pages/AdminDepositsPage'
import { AdminWithdrawalsPage } from './admin/pages/AdminWithdrawalsPage'
import { AdminTransactionsPage } from './admin/pages/AdminTransactionsPage'
import { AdminAuditPage } from './admin/pages/AdminAuditPage'
import { AdminSettingsPage } from './admin/pages/AdminSettingsPage'
import { AdminSystemBackupPage } from './admin/pages/AdminSystemBackupPage'
import { AdminIbPage } from './admin/pages/AdminIbPage'
import { AdminNotificationsPage } from './admin/pages/AdminNotificationsPage'
import { AdminBalancePage } from './admin/pages/AdminBalancePage'
import { IbAuthProvider } from './ib/AuthProvider'
import { IbLayout } from './ib/components/IbLayout'
import { IbApplyPage } from './ib/pages/IbApplyPage'
import { IbLoginPage } from './ib/pages/IbLoginPage'
import { IbForgotPasswordPage } from './ib/pages/IbForgotPasswordPage'
import { IbResetPasswordPage } from './ib/pages/IbResetPasswordPage'
import { IbDashboardPage } from './ib/pages/IbDashboardPage'
import { IbClientsPage } from './ib/pages/IbClientsPage'
import { IbPositionsPage } from './ib/pages/IbPositionsPage'
import { IbWalletPage } from './ib/pages/IbWalletPage'
import { HomePage } from './pages/HomePage'
import { PlaceholderPage } from './pages/PlaceholderPage'
import { WebTestHeroPage } from './web-test/WebTestHeroPage'
import { WebsiteApp } from './web-test/WebsiteApp'
import { PwaRouteSync } from './pwa/PwaRouteSync'

const HowItWorksPage = lazy(async () => ({
  default: () => (
    <PlaceholderPage
      title="How It Works"
      description="Public website content is deferred. Client and Admin panels are the current priority."
    />
  ),
}))

function RouteFallback() {
  return (
    <div className="pp-container" style={{ padding: '4rem 0' }}>
      <p>Loading…</p>
    </div>
  )
}

export default function App() {
  const websitePreview = import.meta.env.VITE_PUBLIC_WEBSITE === '1'

  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminAuthProvider>
          <IbAuthProvider>
            <BrowserRouter>
              <PwaRouteSync />
              <Suspense fallback={<RouteFallback />}>
                {websitePreview ? (
                  <WebsiteApp />
                ) : (
                <Routes>
                  <Route path="/" element={<PublicLayout />}>
                    <Route index element={<HomePage />} />
                    <Route path="how-it-works" element={<HowItWorksPage />} />
                  </Route>

                  <Route path="/app/login" element={<ClientLoginPage />} />
                  <Route path="/app/register" element={<ClientRegisterPage />} />
                  <Route path="/app/verify-email" element={<ClientVerifyEmailPage />} />
                  <Route path="/app/forgot-password" element={<ClientForgotPasswordPage />} />
                  <Route path="/app/reset-password" element={<ClientResetPasswordPage />} />

                  <Route path="/app" element={<ClientLayout />}>
                    <Route index element={<DashboardPage />} />
                    {/* V1: prediction screens kept but removed from journey */}
                    <Route path="predict" element={<Navigate to="/app" replace />} />
                    <Route path="active" element={<Navigate to="/app" replace />} />
                    <Route path="history" element={<Navigate to="/app/trading-history" replace />} />
                    <Route path="trading-history" element={<TradingHistoryPage />} />
                    <Route path="accounts" element={<AccountsPage />} />
                    {/* Create Prediction — inside portal shell (sidebar / topbar stay) */}
                    <Route path="trade" element={<LaunchMt5Page />} />
                    <Route path="deposits" element={<DepositsPage />} />
                    <Route path="withdrawals" element={<WithdrawalsPage />} />
                    <Route path="transactions" element={<TransactionsPage />} />
                    <Route path="support" element={<SupportPage />} />
                    <Route path="profile" element={<ProfilePage />} />
                    <Route path="more" element={<MoreMenu />} />
                  </Route>

                  <Route path="/ib/apply" element={<IbApplyPage />} />
                  <Route path="/ib/login" element={<IbLoginPage />} />
                  <Route path="/ib/forgot-password" element={<IbForgotPasswordPage />} />
                  <Route path="/ib/reset-password" element={<IbResetPasswordPage />} />
                  <Route path="/ib" element={<IbLayout />}>
                    <Route index element={<IbDashboardPage />} />
                    <Route path="clients" element={<IbClientsPage />} />
                    <Route path="positions" element={<IbPositionsPage />} />
                    <Route path="wallet" element={<IbWalletPage />} />
                  </Route>

                  <Route path="/admin/login" element={<AdminLoginPage />} />
                  <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
                  <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
                  <Route path="/admin" element={<AdminLayout />}>
                    <Route index element={<AdminDashboardPage />} />
                    <Route path="clients" element={<AdminClientsPage />} />
                    <Route path="clients/:id" element={<AdminClientDetailPage />} />
                    <Route path="trading-accounts" element={<AdminTradingAccountsPage />} />
                    <Route path="trading-accounts/:id" element={<AdminTradingAccountDetailPage />} />
                    <Route path="deposits" element={<AdminDepositsPage />} />
                    <Route path="withdrawals" element={<AdminWithdrawalsPage />} />
                    <Route path="balance" element={<AdminBalancePage />} />
                    <Route path="ib" element={<AdminIbPage />} />
                    <Route path="notifications" element={<AdminNotificationsPage />} />
                    <Route path="transactions" element={<AdminTransactionsPage />} />
                    <Route path="audit" element={<AdminAuditPage />} />
                    <Route path="system" element={<AdminSystemBackupPage />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                  </Route>

                  {/* Isolated public website hero nano-test — not production site */}
                  <Route path="/web-test" element={<WebTestHeroPage />} />

                  <Route path="*" element={<Navigate to="/app" replace />} />
                </Routes>
                )}
              </Suspense>
            </BrowserRouter>
          </IbAuthProvider>
        </AdminAuthProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}
